import { useInstantSearch } from 'react-instantsearch';

import HorizontalScroller from '@components/application/horizontalScroller';
import InstantSearchVehicle from '@javascript/instant_search/instantSearchVehicle';
import { VehicleShowcaseLoading } from '@javascript/components/vehicle_showcase/VehicleShowcaseLoading';

import useRecommendations from './hooks/useRecommendations';

import Analytics from '@javascript/lib/analytics';

import type { InstantSearchResult } from '@javascript/types/InstantSearch';
import type { WithExperiments } from '@javascript/types/Experiment';

import aa from 'search-insights';

interface OurRecommendationsShowcaseProps {
  cashPriceRange: string;
  config: {
    apiKey: string;
    appId: string;
    indexName: string;
  };
  stockReference: string;
  userToken: string;
}

const OurRecommendationsShowcase = ({
  cashPriceRange,
  config,
  experiments,
  stockReference,
  userToken,
}: OurRecommendationsShowcaseProps & WithExperiments) => {
  const recommendedVehicles = useRecommendations(
    stockReference,
    cashPriceRange,
  );
  const { status } = useInstantSearch();

  const googleAnalytics = ({
    position,
    make,
    model,
    source,
  }: {
    make: string;
    model: string;
    position: number;
    source?: string;
  }) => {
    Analytics.trackEvent('interaction', {
      action: `${make} ${model} at position ${position} clicked from ${source}`,
      label: 'our_recommendations_carousel',
    });
  };

  if (status === 'loading') return <VehicleShowcaseLoading />;

  if (!recommendedVehicles || recommendedVehicles.length === 0) return null;

  const handleAlgoliaEvent = (
    _event: string,
    vehicle: InstantSearchResult,
    _label: string,
    position: number,
  ) => {
    if (vehicle.__queryID) {
      aa('clickedObjectIDsAfterSearch', {
        eventName: 'Recommend Carousel Clicked',
        index: config.indexName,
        objectIDs: [vehicle.objectID],
        positions: [position],
        queryID: vehicle.__queryID,
        userToken,
      });
    }
  };

  return (
    <>
      <div className="ac-vehicle-gallery ch-mb--6">
        <div className="ac-vehicle-gallery__recommendations ch-fs--5 ch-mv--3 sm:ch-mt--6 ch-fw--500">
          Our recommendations
        </div>
        <HorizontalScroller id="our-recommendations">
          {recommendedVehicles.map(
            (vehicle: InstantSearchResult, index: number) => (
              <InstantSearchVehicle
                analytics={() =>
                  googleAnalytics({
                    position: index,
                    make: vehicle.make,
                    model: vehicle.model,
                    source: vehicle.source,
                  })
                }
                experiments={experiments}
                hideImageSlider
                hideStockChip
                hit={vehicle}
                isShowcaseContent
                key={vehicle.objectID}
                sendEvent={(event, hit, label) =>
                  handleAlgoliaEvent(event, hit, label, index + 1)
                }
              />
            ),
          )}
        </HorizontalScroller>
      </div>
      <hr className="ch-bg--grey-3" />
    </>
  );
};

export default OurRecommendationsShowcase;
